'use client'

import { useFollowing } from '../../hooks/useFollowing'

import { useModalContext } from '@/shared/contexts/Modal'
import { useAppRouter } from '@/shared/lib/router'
import { cn } from '@/shared/lib/utils'
import type { User } from '@/shared/types/user'
import { Button } from '@/shared/ui/Button'
import { CosmeticAvatarComposite } from '@/shared/ui/CosmeticAvatarComposite'

import { UserModal } from './Modal'

const FRIENDS_DISPLAY_LIMIT = 3

interface FriendsSectionProps {
  currentUserUid: string
}

export function FriendsSection({ currentUserUid }: FriendsSectionProps) {
  const { openModal, closeModal } = useModalContext()
  const { push } = useAppRouter()
  const { records, isLoading, error, refetch } = useFollowing({
    enabled: true
  })

  const recentFriends = records.slice(0, FRIENDS_DISPLAY_LIMIT)

  const handleOpenAllFriends = () => {
    closeModal()
    push('/amigos')
  }

  const handleOpenSearch = () => {
    closeModal()
    push('/amigos?tab=search')
  }

  const handleProfileClick = (uid: string) => {
    openModal({ modal: UserModal }, { userUid: uid })
  }

  const countLabel =
    records.length === 1 ? '1 amigo' : `${records.length} amigos`

  return (
    <section
      className="flex flex-col pt-4 border-t border-gray-200"
      aria-label="Amigos"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-gray-700">
          {isLoading ? '…' : countLabel}
        </span>
        <Button variant="primary"
          type="button"
          onClick={handleOpenSearch}
          className="text-xs py-1 px-2 rounded-md"
        >
          + Adicionar
        </Button>
      </div>

      {isLoading && (
        <div
          className="rounded-lg bg-jacaranda/20 p-2 flex flex-col gap-1"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="h-8 rounded bg-white/50 animate-pulse" />
          <div className="h-8 rounded bg-white/50 animate-pulse" />
          <div className="h-8 rounded bg-white/50 animate-pulse" />
        </div>
      )}

      {!isLoading && error && (
        <div className="text-xs text-destructive py-1.5 rounded-lg bg-jacaranda/20 px-2">
          Erro ao carregar.
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-1 underline font-medium"
          >
            Tentar
          </button>
        </div>
      )}

      {!isLoading && !error && records.length === 0 && (
        <p className="text-xs text-gray-600 py-2 px-2 rounded-lg bg-jacaranda/20">
          Nenhum amigo ainda.
        </p>
      )}

      {!isLoading && !error && recentFriends.length > 0 && (
        <ul
          className="rounded-lg bg-jacaranda/20 overflow-hidden list-none p-0 m-0"
          aria-label="Amigos recentes"
        >
          {recentFriends.map((user, index) => (
            <li
              key={user.uid}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-jacaranda/30 transition-colors',
                index > 0 && 'border-t-2 border-jacaranda/50'
              )}
              role="button"
              tabIndex={0}
              onClick={() => handleProfileClick(user.uid)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleProfileClick(user.uid)
                }
              }}
              aria-label={`Ver perfil de ${user.name}`}
            >
              <CosmeticAvatarComposite
                avatar={user.avatar}
                frame={user.frame}
                background={user.background}
                size="xs"
                className="size-6 shrink-0"
              />
              <span className="text-xs font-medium text-gray-900 line-clamp-1 min-w-0">
                {user.name}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!isLoading && !error && records.length > 0 && (
        <div className="flex justify-center -mt-2">
          <Button variant="primary"
            type="button"
            onClick={handleOpenAllFriends}
            className="text-xs py-1 px-3 rounded-md"
          >
            Ver todos
          </Button>
        </div>
      )}
    </section>
  )
}
